// Defaults & Workout rebuild — the chronological timeline spine. Every
// artifact (events, notes, cure periods, plans, forbearance, DACA, legal,
// engagements, Article 9, costs, recoveries, closure) renders as a typed
// entry, newest first, filterable by type; each entry opens its artifact
// in the right drawer (deep-linkable via ?artifact=<type>:<id>).

import { useMemo, useState } from "react";
import { Badge } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { useDateFormatter } from "@/utils/datetime";
import {
  TIMELINE_TYPE_LABELS,
  type CaseTimelineEntry,
  type TimelineEntryType,
} from "./timeline";

const TYPE_BADGE: Record<TimelineEntryType, "info" | "draft" | "review" | "gold" | "error" | "finalized"> = {
  event: "info",
  note: "draft",
  "cure-period": "review",
  "remediation-plan": "gold",
  forbearance: "gold",
  daca: "review",
  "legal-action": "review",
  engagement: "info",
  "article-9": "review",
  cost: "error",
  recovery: "finalized",
  // Never actually reached: a report-request entry always supplies its own
  // per-entry badge (its status). Present because the map is exhaustive over
  // TimelineEntryType, which is what makes a new type impossible to add
  // without deciding its tone.
  "report-request": "info",
  closure: "finalized",
};

const fmtCurrency = (n: number): string =>
  formatCurrency(n, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface CaseTimelineProps {
  entries: CaseTimelineEntry[];
  isLoading: boolean;
  onOpen: (key: string) => void;
}

export function CaseTimeline({ entries, isLoading, onOpen }: CaseTimelineProps) {
  const { date } = useDateFormatter();
  const [filter, setFilter] = useState<TimelineEntryType | "">("");

  const presentTypes = useMemo(() => {
    const set = new Set(entries.map((e) => e.type));
    return (Object.keys(TIMELINE_TYPE_LABELS) as TimelineEntryType[]).filter((t) =>
      set.has(t),
    );
  }, [entries]);

  const visible = filter ? entries.filter((e) => e.type === filter) : entries;

  return (
    <div
      className="border border-border-light rounded-md bg-white-pure p-space-3 flex flex-col gap-space-2"
      data-testid="case-timeline"
    >
      <div className="flex items-center justify-between gap-space-2 flex-wrap">
        <h3 className="font-display font-bold text-h4 m-0">Case timeline</h3>
        <div
          className="flex items-center gap-space-1 flex-wrap"
          role="group"
          aria-label="Filter timeline by type"
        >
          <FilterChip
            label="All"
            active={filter === ""}
            onClick={() => setFilter("")}
            testId="timeline-filter-all"
          />
          {presentTypes.map((t) => (
            <FilterChip
              key={t}
              label={TIMELINE_TYPE_LABELS[t]}
              active={filter === t}
              onClick={() => setFilter(filter === t ? "" : t)}
              testId={`timeline-filter-${t}`}
            />
          ))}
        </div>
      </div>

      {isLoading && (
        <p className="text-small text-gray-mid italic m-0">Loading timeline…</p>
      )}
      {!isLoading && visible.length === 0 && (
        <p className="text-small text-gray-mid italic m-0">
          {filter
            ? "No entries of this type."
            : "No activity recorded yet. Use the action rail to start the record."}
        </p>
      )}
      {visible.length > 0 && (
        <ol className="m-0 p-0 list-none" data-testid="case-timeline-list">
          {visible.map((e) => (
            <li key={e.key} className="border-b border-border-light last:border-0">
              <button
                type="button"
                onClick={() => onOpen(e.key)}
                className="w-full text-left py-space-2 px-space-1 flex items-start gap-space-3 hover:bg-white-warm rounded"
                data-testid={`timeline-entry-${e.key}`}
              >
                <span className="shrink-0 w-24 text-caption text-gray-mid pt-[2px]">
                  {date(e.date)}
                </span>
                <span className="shrink-0">
                  <Badge status={e.badge?.status ?? TYPE_BADGE[e.type]}>
                    {e.badge?.label ?? TIMELINE_TYPE_LABELS[e.type]}
                  </Badge>
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-small font-bold truncate">{e.title}</span>
                  {e.detail && (
                    <span className="block text-caption text-gray-mid truncate">
                      {e.detail}
                    </span>
                  )}
                  {e.attention && (
                    <span className="block text-caption text-status-warning truncate">
                      {e.attention}
                    </span>
                  )}
                </span>
                {e.amount !== null && e.amount !== undefined && (
                  <span className="shrink-0 text-small font-bold tabular-nums text-right">
                    {fmtCurrency(e.amount)}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
  testId,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center h-6 px-space-2 rounded-full text-caption border ${
        active
          ? "bg-onyx text-white-pure border-onyx"
          : "bg-white-pure text-gray-mid border-border-light hover:bg-white-warm"
      }`}
    >
      {label}
    </button>
  );
}
